import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';

/**
 * Seccion Insumos + Pedidos internos: stock y bajo minimo, consumo del
 * periodo por area, insumos mas usados, y estado de pedidos internos.
 */

const money = (n: number) => {
  if (n == null) return '—';
  if (Math.abs(n) >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (Math.abs(n) >= 1e3) return `$${Math.round(n / 1e3)}k`;
  return `$${Math.round(n).toLocaleString('es-AR')}`;
};

const PEDIDO_LABEL: Record<string, string> = {
  pending: 'Pendientes', approved: 'Aprobados', delivered: 'Entregados',
  completed: 'Completados', rejected: 'Rechazados', cancelled: 'Cancelados'
};

export default function InsumosSection({ startDate, endDate }: { startDate: string; endDate: string }) {
  const [d, setD] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!startDate || !endDate || startDate > endDate) return;
    setError('');
    setLoading(true);
    axios.get('/api/reports/tablero/insumos', { params: { startDate, endDate } })
      .then((r) => setD(r.data))
      .catch((e) => setError(e.response?.data?.error || 'Error cargando insumos'))
      .finally(() => setLoading(false));
  }, [startDate, endDate]);

  if (loading && !d) return <div className="py-10 text-center text-muted-foreground">Cargando insumos...</div>;
  if (error) return <div className="py-10 text-center text-red-600">{error}</div>;
  if (!d) return null;

  return (
    <div className={`space-y-4 transition-opacity duration-200 ${loading ? 'opacity-60 pointer-events-none' : ''}`}>
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-xs text-muted-foreground mb-1">Insumos registrados</p>
          <p className="text-2xl font-semibold leading-none mb-1.5">{d.kpis.insumos.value}</p>
          <span className="text-xs text-muted-foreground">activos en inventario</span>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-xs text-muted-foreground mb-1">Bajo mínimo</p>
          <p className={`text-2xl font-semibold leading-none mb-1.5 ${d.kpis.bajoMinimo.value > 0 ? 'text-red-600' : ''}`}>{d.kpis.bajoMinimo.value}</p>
          <span className="text-xs text-muted-foreground">para reponer</span>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-xs text-muted-foreground mb-1">Consumo del período</p>
          <p className="text-2xl font-semibold leading-none mb-1.5">{money(d.kpis.consumo.costo)}</p>
          <span className="text-xs text-muted-foreground">{d.kpis.consumo.movimientos} movimientos</span>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-xs text-muted-foreground mb-1">Pedidos internos abiertos</p>
          <p className="text-2xl font-semibold leading-none mb-1.5">{d.kpis.pedidosPendientes.value}</p>
          <span className="text-xs text-muted-foreground">esperando entrega</span>
        </CardContent></Card>
      </div>

      {!d.movimientosCargados && (
        <p className="text-xs text-amber-600 px-1">
          ⚠ Todavía no se registran consumos de insumos. Se cargan desde <span className="underline font-medium">Inventario → Insumos</span> (movimientos de salida) — con eso esta sección muestra consumo por área, costo por servicio y los más usados.
        </p>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardContent className="pt-5">
            <h3 className="font-semibold text-sm mb-3">Consumo por área (período)</h3>
            {d.porArea.length === 0 && <p className="text-sm text-muted-foreground">Sin consumos registrados en el período</p>}
            <div className="space-y-2">
              {d.porArea.map((a: any) => {
                const max = Math.max(1, ...d.porArea.map((x: any) => x.costo));
                return (
                  <div key={a.area}>
                    <div className="flex items-baseline justify-between text-sm mb-0.5">
                      <span>{a.area} <span className="text-xs text-muted-foreground">({a.movimientos})</span></span>
                      <span className="font-medium">{money(a.costo)}</span>
                    </div>
                    <div className="w-full h-1.5 rounded bg-muted overflow-hidden">
                      <div className="h-full rounded" style={{ width: `${(a.costo / max) * 100}%`, background: '#7F77DD' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-5">
            <h3 className="font-semibold text-sm mb-3">Insumos más usados (período)</h3>
            {d.masUsados.length === 0 && <p className="text-sm text-muted-foreground">Sin consumos registrados en el período</p>}
            {d.masUsados.length > 0 && (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-muted-foreground text-left">
                    <th className="pb-1.5 font-normal">Insumo</th>
                    <th className="pb-1.5 font-normal text-right">Unidades</th>
                    <th className="pb-1.5 font-normal text-right">Costo</th>
                  </tr>
                </thead>
                <tbody>
                  {d.masUsados.map((m: any, i: number) => (
                    <tr key={i} className="border-t">
                      <td className="py-1.5 pr-2 truncate max-w-[220px]">{m.name}</td>
                      <td className="py-1.5 pr-2 text-right text-muted-foreground">{m.unidades}</td>
                      <td className="py-1.5 text-right font-medium">{money(m.costo)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardContent className="pt-5">
            <h3 className="font-semibold text-sm mb-3">Stock bajo mínimo</h3>
            {d.bajoMinimo.length === 0 && <p className="text-sm text-muted-foreground">Nada por debajo del mínimo. 🎉</p>}
            {d.bajoMinimo.length > 0 && (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-muted-foreground text-left">
                    <th className="pb-1.5 font-normal">Insumo</th>
                    <th className="pb-1.5 font-normal text-right">Stock</th>
                    <th className="pb-1.5 font-normal text-right">Mínimo</th>
                  </tr>
                </thead>
                <tbody>
                  {d.bajoMinimo.map((b: any, i: number) => (
                    <tr key={i} className="border-t">
                      <td className="py-1.5 pr-2">{b.name}</td>
                      <td className="py-1.5 pr-2 text-right text-red-600 font-medium">{b.stock} {b.unit}</td>
                      <td className="py-1.5 text-right text-muted-foreground">{b.minStock} {b.unit}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-5">
            <h3 className="font-semibold text-sm mb-3">Pedidos internos</h3>
            {d.pedidos.length === 0 && <p className="text-sm text-muted-foreground">Sin pedidos internos registrados. Se cargan desde Inventario → Pedidos internos.</p>}
            <div className="space-y-2">
              {d.pedidos.map((p: any) => {
                const max = Math.max(1, ...d.pedidos.map((x: any) => x.n));
                return (
                  <div key={p.status}>
                    <div className="flex items-baseline justify-between text-sm mb-0.5">
                      <span>{PEDIDO_LABEL[p.status] || p.status}</span>
                      <span className="text-muted-foreground text-xs">{p.n}</span>
                    </div>
                    <div className="w-full h-1.5 rounded bg-muted overflow-hidden">
                      <div className="h-full rounded bg-primary/70" style={{ width: `${(p.n / max) * 100}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
